import EventBanner from '@assets/event-banner.png';
import { CancelContent } from '@components/display/dialogs/CancelContent';
import DottedBackground from '@components/layout/DottedBackground/DottedBackground';
import { useMaxWidthWrapperBottomElement } from '@components/layout/MaxWidthWrapper';
import { Tabs } from '@components/utility/tabs';
import { EventDetailMarketCard } from '@event/components/display/cards';
import {
    BringColleagueDialog,
    RequiredEventsDialog,
    TemporaryRequiredEventsDialog,
    UnauthenticatedFormDialog,
    UnauthenticatedMode,
} from '@event/components/display/dialogs';
import { ColleagueDialog } from '@event/components/display/dialogs/ColleagueDialog';
import { HybridEventDialog } from '@event/components/display/dialogs/HybridEventDialog';
import { FailedStatus, RegistrationFailedDialog } from '@event/components/display/dialogs/RegistrationFailedDialog';
import { COLLEAGUE_HASH_KEY, GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { usePublicEventContext } from '@event/contexts/PublicEventContext';
import { useQuizz } from '@event/hooks/useQuizz';
import { EventDetailCard } from '@event/routes/public/EventDetail/EventDetailCard';
import { useLazyGetPublicEventQuery } from '@event/services/events';
import { EventTypes, RegistrationCardStatus, WaitingListProps } from '@event/types/event';
import { getOppositeEventType } from '@event/utils/event';
import useExtensions from '@hooks/useExtensions';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Button, CircularProgress, Dialog, IconButton, Tabs as MUITabs, Typography } from '@mui/material';
import { Widget } from '@typeform/embed-react';
import { handleError, toaster } from '@utils/common';
import { toLogin } from '@utils/route';
import { isApiResponseError } from '@utils/service';
import clsx from 'clsx';
import { RichTextReadOnly } from 'mui-tiptap';
import { createPortal } from 'react-dom';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams, useSearchParams } from 'react-router-dom';

const TAB_CLASSNAME = 'font-inter font-bold text-sm uppercase';

const EventDetailCardView = () => {
    const [failedStatus, setFailedStatus] = useState<FailedStatus>();
    const [unauthenticatedFormMode, setUnauthenticatedFormMode] = useState<null | UnauthenticatedMode>(null);
    const [hybridRegistrationDetails, setHybridRegistrationDetails] = useState<{
        email: string;
        first_name: string;
        last_name: string;
        company_name?: string;
    }>();
    const [currentTab, setCurrentTab] = useState(0);
    const [isHybridEventDialogOpen, setIsHybridEventDialogOpen] = useState(false);
    const [colleagueDialogOpen, setColleagueDialogOpen] = useState(false);
    const [requiredEventsDialogOpen, setRequiredEventsDialogOpen] = useState(false);
    const [temporaryRequiredEventsDialogState, setTemporaryRequiredEventsDialogState] = useState<
        false | 'view' | 'edit'
    >(false);
    const [isWaitingListFlow, setIsWaitingListFlow] = useState(false);
    const [bringAColleagueDialogOpen, setBringAColleagueDialogOpen] = useState(false);

    const registrationActionsContainer = useMaxWidthWrapperBottomElement();

    const { type, eventId } = useParams();
    const eventType = type as EventTypes;

    const [searchParams, setSearchParams] = useSearchParams();
    const colleagueHash = useMemo(() => searchParams.get(COLLEAGUE_HASH_KEY), [searchParams]);
    const guestAccessHashParam = useMemo(() => searchParams.get(GUEST_ACCESS_HASH_KEY), [searchParams]);

    const extensions = useExtensions();
    const intl = useIntl();

    const {
        event: { data: event, loading: isEventLoading },
        oppositeEvent: oppositeEventData,
        user: { data: user, loading: isUserLoading },
        guestUserInfo: { data: guestUserInfo, fetching: isGuestUserInfoFetching },
        isLoggedIn,
        toastProps,
        register: { fn: registerUser, loading: isRegisterLoading },
        unregister: { fn: unregisterUser, loading: isUnregisterLoading },
        joinWaitingList: { fn: joinWaitingList, loading: isJoiningWaitingList },
        leaveWaitingList: { fn: leaveWaitingList, loading: isLeavingWaitingList },
        waitingListSuccess: { setState: setWaitingListSuccessState },
    } = usePublicEventContext();
    const { data: oppositeEvent, loading: isOppositeEventLoading } = oppositeEventData || {};

    const [getEvent, { isLoading: isLazyEventLoading }] = useLazyGetPublicEventQuery();

    const register = useCallback(async () => {
        try {
            await registerUser({
                type: eventType,
                id: Number(eventId),
                colleagueHash,
                guestAccessHash: guestAccessHashParam,
            }).unwrap();
            toaster.success({
                msg: intl.formatMessage({
                    id: 'event.registered',
                    defaultMessage: 'You are now registered',
                }),
            });
            setUnauthenticatedFormMode(null);
        } catch (err) {
            if (isApiResponseError(err) && Array.isArray(err.data)) {
                // In case it fails during unauthenticated registration, close the form
                setUnauthenticatedFormMode(null);
                if (err.data[0]?.includes('seats available')) {
                    setFailedStatus(FailedStatus.MAX_CAPACITY);
                } else if (err.data[0]?.includes(FailedStatus.LOGIN)) {
                    setFailedStatus(FailedStatus.LOGIN);
                } else {
                    handleError(
                        intl.formatMessage({
                            id: 'event.registrationError',
                            defaultMessage: 'Error registering for event',
                        }),
                        err
                    );
                }
            }
        }
    }, [colleagueHash, eventId, eventType, guestAccessHashParam, registerUser, toastProps, intl]);

    const handleJoinWaitingList = useCallback(
        async (formValues?: Omit<WaitingListProps, 'type' | 'id' | 'colleagueHash' | 'guestAccessHashParam'>) => {
            try {
                let body: WaitingListProps = {
                    type: eventType,
                    id: Number(eventId),
                    colleagueHash,
                    guestAccessHash: guestAccessHashParam,
                };
                const authUser = guestUserInfo || user;

                // Is the user authenticated?
                if (isLoggedIn || guestUserInfo) {
                    if (
                        authUser?.email?.trim() &&
                        authUser.first_name?.trim() &&
                        authUser.last_name?.trim() &&
                        authUser.company_name?.trim()
                    ) {
                        // Add user details to the body for guest users only
                        // For logged-in users, the server will use their authenticated details
                        if (!isLoggedIn && guestUserInfo && authUser) {
                            body = {
                                ...body,
                                email: authUser.email,
                                company_name: authUser.company_name ?? '',
                                first_name: authUser.first_name,
                                last_name: authUser.last_name,
                            };

                            if (authUser.country_id) {
                                body.country_code = authUser.country_id;
                            }
                        }
                    } else {
                        setIsWaitingListFlow(true);
                        setUnauthenticatedFormMode('missing-details');
                        return;
                    }
                } else if (formValues) {
                    // Use form values passed from dialog
                    body = {
                        ...body,
                        email: formValues.email,
                        company_name: formValues.company_name,
                        first_name: formValues.first_name,
                        last_name: formValues.last_name,
                    };

                    if (formValues.country_code) {
                        body.country_code = formValues.country_code;
                    }
                } else {
                    setIsWaitingListFlow(true);
                    setUnauthenticatedFormMode('unauthenticated');
                    return; // Exit early to show form, don't call API yet
                }
                await joinWaitingListAction(body || {});
            } catch (err) {
                if (isApiResponseError(err)) {
                    // In case it fails during unauthenticated waiting list join, close the form
                    setUnauthenticatedFormMode(null);
                    setIsWaitingListFlow(false);
                    if (err.data?.email?.[0]?.includes(FailedStatus.LOGIN)) {
                        setFailedStatus(FailedStatus.LOGIN);
                    } else {
                        handleError(
                            intl.formatMessage({
                                id: 'event.error.joinWaitingList',
                                defaultMessage: 'Error joining waiting list',
                            }),
                            err
                        );
                    }
                }
            }
        },
        [
            colleagueHash,
            eventId,
            eventType,
            guestAccessHashParam,
            guestUserInfo,
            isLoggedIn,
            joinWaitingList,
            user,
            setWaitingListSuccessState,
            intl,
        ]
    );

    const joinWaitingListAction = useCallback(
        async (values?: Omit<WaitingListProps, 'type' | 'id' | 'colleagueHash' | 'guestAccessHashParam'>) => {
            try {
                const response = await joinWaitingList({
                    type: eventType,
                    id: Number(eventId),
                    colleagueHash,
                    guestAccessHash: guestAccessHashParam,
                    ...values,
                }).unwrap();
                setWaitingListSuccessState('join');

                const { guest_access_hash } = response;
                if (guest_access_hash && !isLoggedIn) {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.set(GUEST_ACCESS_HASH_KEY, guest_access_hash);
                        return params;
                    });
                }

                setWaitingListSuccessState('join');
                setUnauthenticatedFormMode(null);
                setIsWaitingListFlow(false);
            } catch (err) {
                if (isApiResponseError(err) && Array.isArray(err.data)) {
                    setUnauthenticatedFormMode(null);
                    setIsWaitingListFlow(false);
                    if (err.data[0]?.includes('seats available')) {
                        setFailedStatus(FailedStatus.MAX_CAPACITY);
                    } else if (err.data[0]?.includes(FailedStatus.LOGIN)) {
                        setFailedStatus(FailedStatus.LOGIN);
                    } else {
                        handleError(
                            intl.formatMessage({
                                id: 'event.error.joinWaitingList',
                                defaultMessage: 'Error joining waiting list',
                            }),
                            err
                        );
                    }
                }
            }
        },
        [colleagueHash, eventId, eventType, guestAccessHashParam, joinWaitingList, setWaitingListSuccessState, intl]
    );

    const {
        quizzStatus,
        setQuizzStatus,
        handleClose,
        handleGoBack,
        handleIconClose,
        handleStarted,
        handleSubmit,
        quizzDialogOpen,
        setQuizzDialogOpen,
    } = useQuizz({
        isAuthenticated: isLoggedIn || !!guestUserInfo,
        handleRegistration: register,
    });

    const handleUnauthQuizz = (values?: {
        first_name: string;
        last_name: string;
        email: string;
        company_name?: string;
        country_code?: string;
    }) => {
        setQuizzDialogOpen(true);
        setQuizzStatus((prev) => ({
            ...prev,
            guestInfo: values,
            show: true,
        }));
    };

    const handleUnregister = useCallback(async () => {
        try {
            await unregisterUser({
                type: eventType,
                id: Number(eventId),
                colleagueHash,
                guestAccessHash: guestAccessHashParam,
            }).unwrap();
            toaster.warning({
                msg: intl.formatMessage({ id: 'event.unregistered', defaultMessage: 'You are now unregistered' }),
            });
        } catch (err) {
            handleError(
                intl.formatMessage({ id: 'event.error.unregister', defaultMessage: 'Error unregistering from event' }),
                err
            );
        }
    }, [colleagueHash, eventId, eventType, guestAccessHashParam, toastProps, unregisterUser, intl]);

    const handleRegister = useCallback(async () => {
        try {
            const { number_attendees, max_capacity } = await getEvent({
                type: eventType,
                id: Number(eventId),
                colleagueHash,
                guestAccessHash: guestAccessHashParam,
            }).unwrap();

            // Has the registration capacity been reached?
            if (Number(max_capacity) <= Number(number_attendees)) {
                setFailedStatus(FailedStatus.MAX_CAPACITY);
            } else {
                // Is the user authenticated?
                if (isLoggedIn || guestUserInfo) {
                    const authUser = guestUserInfo || user;

                    // Are all Primary User Details known?
                    if (
                        authUser?.email?.trim() &&
                        authUser.first_name?.trim() &&
                        authUser.last_name?.trim() &&
                        authUser.company_name?.trim()
                    ) {
                        // Will registration include a questionnaire?
                        if (event?.enable_questionnaire) {
                            // Is the user re-registering? (User has been registered to this event before)
                            if (!!event.subscriber_status_updated_at) {
                                await register();
                            } else {
                                setQuizzDialogOpen(true);
                                setQuizzStatus((prev) => ({
                                    ...prev,
                                    guestInfo: guestUserInfo
                                        ? {
                                              ...guestUserInfo,
                                              company_name: guestUserInfo.company_name || '',
                                          }
                                        : undefined,
                                    show: true,
                                }));
                            }
                        } else {
                            await register();
                        }
                    } else {
                        setUnauthenticatedFormMode('missing-details');
                    }
                } else {
                    setUnauthenticatedFormMode('unauthenticated');
                }
            }
        } catch (err) {
            handleError(
                intl.formatMessage({ id: 'event.error.registration', defaultMessage: 'Error registering for event' }),
                err
            );
        }
    }, [
        colleagueHash,
        event?.enable_questionnaire,
        event?.subscriber_status_updated_at,
        eventId,
        eventType,
        getEvent,
        guestAccessHashParam,
        guestUserInfo,
        isLoggedIn,
        register,
        setQuizzDialogOpen,
        setQuizzStatus,
        user,
        intl,
    ]);

    const handleOpenRequiredEvents = () => {
        setRequiredEventsDialogOpen(true);
    };

    const handleExternalRegistration = useCallback(
        () => (window.location.href = event?.registration_url ?? ''),
        [event?.registration_url]
    );

    const handleLeaveWaitinglist = useCallback(async () => {
        try {
            await leaveWaitingList({
                type: eventType,
                id: Number(eventId),
                colleagueHash,
                guestAccessHash: guestAccessHashParam,
            }).unwrap();

            if (!isLoggedIn && guestAccessHashParam) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.delete(GUEST_ACCESS_HASH_KEY);
                    return params;
                });
            }

            setWaitingListSuccessState('leave');
        } catch (err) {
            handleError(
                intl.formatMessage({
                    id: 'event.error.waitingList.leave',
                    defaultMessage: 'Error leaving waiting list',
                }),
                err
            );
        }
    }, [
        leaveWaitingList,
        eventType,
        eventId,
        colleagueHash,
        guestAccessHashParam,
        isLoggedIn,
        setSearchParams,
        setWaitingListSuccessState,
    ]);

    const HybridEventManagementSubscriptionElement = useMemo(() => {
        const registeredEvent =
            event?.registration_status === RegistrationCardStatus.REGISTERED ? event : oppositeEvent;
        const registeredEventType =
            event?.registration_status === RegistrationCardStatus.REGISTERED
                ? eventType
                : getOppositeEventType(eventType);

        return (
            <>
                <Alert severity="info">
                    <Typography fontFamily="Roboto" fontWeight={500} fontSize={16} letterSpacing={0.15}>
                        <FormattedMessage
                            id="event.hybrid.registered"
                            defaultMessage="You are registered for {name} {type} event."
                            values={{
                                name: registeredEvent?.name,
                                type: (
                                    <Typography component="span" fontWeight={700}>
                                        {[EventTypes.PRIVATE_ONSITE, EventTypes.PUBLIC_ONSITE].includes(
                                            registeredEventType
                                        )
                                            ? 'Onsite'
                                            : 'Virtual'}
                                    </Typography>
                                ),
                            }}
                        />
                    </Typography>
                    <Typography fontFamily="Roboto" fontSize={14} letterSpacing={0.17}>
                        <FormattedMessage
                            id="event.hybrid.manageRegistration.info"
                            defaultMessage='Your email has already been registered to this event. To change between Onsite and Online attendance click the "Manage Registration" button.'
                        />
                    </Typography>
                </Alert>
                <div className="flex gap-2 mt-5 w-full">
                    {event?.enable_bring_a_colleague_button ? (
                        <Button
                            fullWidth
                            className="rounded-md px-0 uppercase"
                            disableElevation={false}
                            onClick={() => setBringAColleagueDialogOpen(true)}
                            size="large"
                        >
                            <Typography fontSize={15} fontFamily="Roboto" fontWeight={500} letterSpacing={0.46}>
                                <FormattedMessage id="event.bringAColleague" defaultMessage="Bring A Colleague" />
                            </Typography>
                        </Button>
                    ) : null}
                    <Button
                        fullWidth
                        disableElevation={false}
                        className="rounded-md h-[42px] uppercase ml-auto"
                        onClick={() => setIsHybridEventDialogOpen(true)}
                    >
                        <Typography fontFamily="Roboto" fontWeight={500} fontSize={15} letterSpacing={0.46}>
                            <FormattedMessage
                                id="event.hybrid.manageRegistration"
                                defaultMessage="Manage Registration"
                            />
                        </Typography>
                    </Button>
                </div>
            </>
        );
    }, [event, eventType, oppositeEvent]);

    const handleTemporaryRegistration = () => {
        setTemporaryRequiredEventsDialogState('edit');
    };

    const handleHybridRegisterNow = useCallback(() => {
        if (!user && !guestUserInfo) {
            setUnauthenticatedFormMode('hybrid');
            return;
        }
        setIsHybridEventDialogOpen(true);
    }, [guestUserInfo, user]);

    const handleHybridRegistrationForm = useCallback(
        (formValues: { email: string; first_name: string; last_name: string; company_name?: string }) => {
            setHybridRegistrationDetails(formValues);
            setUnauthenticatedFormMode(null);
            setIsHybridEventDialogOpen(true);
        },
        []
    );

    const RegistrationActionView = useMemo(() => {
        if (
            isRegisterLoading ||
            isUnregisterLoading ||
            isEventLoading ||
            isUserLoading ||
            isGuestUserInfoFetching ||
            isLazyEventLoading ||
            isOppositeEventLoading
        ) {
            return <CircularProgress />;
        }

        const triggerTemporaryRegistration =
            event?.allow_temporary_registration &&
            event?.registration_status &&
            event?.registration_status === RegistrationCardStatus.ABLE_TO_REGISTER &&
            event?.user_meets_level_badge_requirements?.meets_requirements_without_temp_badges === false &&
            event?.user_meets_level_badge_requirements?.meets_requirements === true;

        if (!event?.published) {
            /**
             * CARD_A1
             */
            return (
                <div className="bg-light-blue py-3 px-4 rounded flex gap-2 w-full">
                    <InfoOutlinedIcon color="info-main" />
                    <div>
                        <Typography
                            fontFamily="Roboto"
                            fontSize={16}
                            fontWeight={500}
                            letterSpacing={0.15}
                            color="alert-info.main"
                            marginBottom={1}
                        >
                            <FormattedMessage id="event.publish.notPublished" defaultMessage="Event not published" />
                        </Typography>
                        <Typography fontFamily="Roboto" fontSize={14} letterSpacing={0.17} color="alert-info.main">
                            <FormattedMessage
                                id="event.publish.info"
                                defaultMessage="Publish your event to make it visible to a broader audience beyond just admins and sales managers."
                            />
                        </Typography>
                    </div>
                </div>
            );
        }

        switch (event?.registration_status) {
            case RegistrationCardStatus.AUTHENTICATION_NEEDED:
                return (
                    <Button
                        disableElevation={false}
                        className="rounded-md md:min-w-[212px] uppercase"
                        size="large"
                        component={Link}
                        to={toLogin()}
                    >
                        <Typography
                            className="uppercase"
                            fontFamily="Roboto"
                            fontWeight={500}
                            fontSize={15}
                            letterSpacing={0.46}
                        >
                            <FormattedMessage id="common.login" defaultMessage="Login" />
                        </Typography>
                    </Button>
                );
            case RegistrationCardStatus.REGISTERED:
                if (event?.hybrid_event) {
                    return HybridEventManagementSubscriptionElement;
                } else if (event?.user_has_temporary_registration) {
                    return (
                        <div className="w-full">
                            <Alert severity="info">
                                <Typography fontFamily="Roboto" fontSize={16} letterSpacing={0.15} fontWeight={500}>
                                    <FormattedMessage
                                        id="event.temporary.registered"
                                        defaultMessage="You're temporarily registered!"
                                    />
                                </Typography>
                                <Typography fontFamily="Roboto" fontSize={14} letterSpacing={0.17} mt={1}>
                                    <FormattedMessage
                                        id="event.temporary.confirm"
                                        defaultMessage="To confirm your attendance, you must first attend one of the required events you're already registered for."
                                    />
                                </Typography>
                            </Alert>
                            <div className="grid grid-cols-2 gap-2 mt-5 w-full flex-wrap">
                                {event.enable_bring_a_colleague_button ? (
                                    <Button
                                        fullWidth
                                        className="rounded-md px-0 uppercase"
                                        disableElevation={false}
                                        onClick={() => setBringAColleagueDialogOpen(true)}
                                        size="large"
                                    >
                                        <Typography
                                            fontSize={15}
                                            fontFamily="Roboto"
                                            fontWeight={500}
                                            letterSpacing={0.46}
                                        >
                                            <FormattedMessage
                                                id="event.bringAColleague"
                                                defaultMessage="Bring A Colleague"
                                            />
                                        </Typography>
                                    </Button>
                                ) : (
                                    <Button
                                        fullWidth
                                        className="rounded-md px-0 uppercase"
                                        disableElevation={false}
                                        onClick={() => setTemporaryRequiredEventsDialogState('view')}
                                        size="large"
                                    >
                                        <Typography
                                            fontSize={15}
                                            fontFamily="Roboto"
                                            fontWeight={500}
                                            letterSpacing={0.46}
                                        >
                                            <FormattedMessage
                                                id="event.viewRequiredEvents"
                                                defaultMessage="View Required Events"
                                            />
                                        </Typography>
                                    </Button>
                                )}
                                <Button
                                    fullWidth
                                    disableElevation={false}
                                    color="neutral-650"
                                    className="rounded-md px-0 uppercase ml-auto"
                                    onClick={handleUnregister}
                                    size="large"
                                >
                                    <Typography fontSize={15} fontFamily="Roboto" fontWeight={500} letterSpacing={0.46}>
                                        <FormattedMessage id="event.unregister" defaultMessage="Unregister" />
                                    </Typography>
                                </Button>
                                {event.enable_bring_a_colleague_button ? (
                                    <Button
                                        fullWidth
                                        className="rounded-md px-0 uppercase col-span-2"
                                        disableElevation={false}
                                        color="secondary"
                                        onClick={() => setTemporaryRequiredEventsDialogState('view')}
                                        size="large"
                                    >
                                        <Typography
                                            fontSize={15}
                                            fontFamily="Roboto"
                                            fontWeight={500}
                                            letterSpacing={0.46}
                                        >
                                            <FormattedMessage
                                                id="event.viewRequiredEvents"
                                                defaultMessage="View Required Events"
                                            />
                                        </Typography>
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    );
                } else {
                    return (
                        <>
                            <div className="bg-light-blue py-3 px-4 rounded flex gap-2 w-full">
                                <InfoOutlinedIcon color="info-main" />
                                <Typography
                                    fontSize={16}
                                    fontFamily="Roboto"
                                    fontWeight={500}
                                    letterSpacing={0.15}
                                    color="alert-info.main"
                                >
                                    <FormattedMessage id="event.registered" defaultMessage="You are registered." />
                                </Typography>
                            </div>
                            <div className="flex w-full gap-3">
                                {event.enable_bring_a_colleague_button ? (
                                    <Button
                                        fullWidth
                                        className="rounded-md px-0 uppercase"
                                        disableElevation={false}
                                        onClick={() => setBringAColleagueDialogOpen(true)}
                                        size="large"
                                    >
                                        <Typography
                                            fontSize={15}
                                            fontFamily="Roboto"
                                            fontWeight={500}
                                            letterSpacing={0.46}
                                        >
                                            <FormattedMessage
                                                id="event.bringAColleague"
                                                defaultMessage="Bring A Colleague"
                                            />
                                        </Typography>
                                    </Button>
                                ) : null}
                                <Button
                                    disableElevation={false}
                                    color="neutral-650"
                                    className="rounded-md md:min-w-[212px] uppercase ml-auto"
                                    size="large"
                                    onClick={handleUnregister}
                                >
                                    <Typography fontSize={15} fontFamily="Roboto" fontWeight={500} letterSpacing={0.46}>
                                        <FormattedMessage id="event.unregister" defaultMessage="Unregister" />
                                    </Typography>
                                </Button>
                            </div>
                        </>
                    );
                }
            case RegistrationCardStatus.ABLE_TO_REGISTER:
                if (event.enable_external_registration) {
                    /**
                     * CARD_A2
                     */
                    return (
                        <>
                            <div className="bg-light-blue py-3 px-4 rounded flex gap-2 w-full">
                                <InfoOutlinedIcon color="info-main" />
                                <div>
                                    <Typography
                                        fontFamily="Roboto"
                                        fontSize={16}
                                        fontWeight={500}
                                        letterSpacing={0.15}
                                        color="alert-info.main"
                                        marginBottom={1}
                                    >
                                        <FormattedMessage
                                            id="event.external.title"
                                            defaultMessage="External Registration"
                                        />
                                    </Typography>
                                    <Typography
                                        fontFamily="Roboto"
                                        fontSize={14}
                                        letterSpacing={0.17}
                                        color="alert-info.main"
                                    >
                                        <FormattedMessage
                                            id="event.external.info"
                                            defaultMessage="Registration will take place outside Victron Professional."
                                        />
                                    </Typography>
                                </div>
                            </div>
                            <Button
                                disableElevation={false}
                                color="secondary"
                                className="rounded-md md:min-w-[212px] uppercase"
                                size="large"
                                onClick={handleExternalRegistration}
                            >
                                <Typography
                                    fontSize={15}
                                    fontFamily="Roboto"
                                    fontWeight={500}
                                    letterSpacing={0.46}
                                    color="neutral-50"
                                >
                                    <FormattedMessage id="event.registerNow" defaultMessage="Register Now" />
                                </Typography>
                            </Button>
                        </>
                    );
                } else {
                    if (
                        event.hybrid_event &&
                        oppositeEvent?.registration_status === RegistrationCardStatus.REGISTERED
                    ) {
                        return HybridEventManagementSubscriptionElement;
                    } else {
                        return (
                            <Button
                                disableElevation={false}
                                color="secondary"
                                className="rounded-md md:min-w-[212px] uppercase"
                                size="large"
                                onClick={
                                    event.hybrid_event
                                        ? handleHybridRegisterNow
                                        : triggerTemporaryRegistration
                                        ? handleTemporaryRegistration
                                        : handleRegister
                                }
                            >
                                <Typography
                                    fontSize={15}
                                    fontFamily="Roboto"
                                    fontWeight={500}
                                    letterSpacing={0.46}
                                    color="neutral-50"
                                >
                                    <FormattedMessage id="event.registerNow" defaultMessage="Register Now" />
                                </Typography>
                            </Button>
                        );
                    }
                }
            case RegistrationCardStatus.MAX_CAPACITY_REACHED:
                if (event?.enable_waiting_list) {
                    if (event.is_user_in_waiting_list) {
                        return (
                            <div className="flex gap-3 w-full">
                                {event.enable_bring_a_colleague_button ? (
                                    <Button
                                        fullWidth
                                        className="rounded-md px-0 uppercase"
                                        disableElevation={false}
                                        onClick={() => setBringAColleagueDialogOpen(true)}
                                        size="large"
                                    >
                                        <Typography
                                            fontSize={15}
                                            fontFamily="Roboto"
                                            fontWeight={500}
                                            letterSpacing={0.46}
                                        >
                                            <FormattedMessage
                                                id="event.bringAColleague"
                                                defaultMessage="Bring A Colleague"
                                            />
                                        </Typography>
                                    </Button>
                                ) : null}
                                <Button
                                    fullWidth
                                    disableElevation={false}
                                    color="neutral-650"
                                    variant="contained"
                                    className="rounded-md md:min-w-[212px] uppercase ml-auto"
                                    size="large"
                                    loading={isLeavingWaitingList}
                                    onClick={handleLeaveWaitinglist}
                                >
                                    <Typography
                                        fontSize={15}
                                        fontFamily="Roboto"
                                        fontWeight={500}
                                        letterSpacing={0.46}
                                        color="contrastText"
                                    >
                                        <FormattedMessage
                                            id="event.leaveWaitingList.uppercase"
                                            defaultMessage="Leave Waiting List"
                                        />
                                    </Typography>
                                </Button>
                            </div>
                        );
                    } else {
                        return (
                            <Button
                                disableElevation={false}
                                color="primary-600"
                                className="rounded-md md:min-w-[212px] uppercase"
                                size="large"
                                loading={isJoiningWaitingList}
                                onClick={() => handleJoinWaitingList()}
                            >
                                <Typography fontSize={15} fontFamily="Roboto" fontWeight={500} letterSpacing={0.46}>
                                    <FormattedMessage
                                        id="event.joinWaitingList.uppercase"
                                        defaultMessage="Join Waiting List"
                                    />
                                </Typography>
                            </Button>
                        );
                    }
                } else {
                    return (
                        <div className="bg-light-blue py-3 px-4 rounded flex gap-2 w-full">
                            <InfoOutlinedIcon color="info-main" />
                            <div>
                                <Typography
                                    fontFamily="Roboto"
                                    fontSize={16}
                                    fontWeight={500}
                                    letterSpacing={0.15}
                                    color="alert-info.main"
                                    marginBottom={1}
                                >
                                    <FormattedMessage
                                        id="event.registrationClosed"
                                        defaultMessage="Registration Closed"
                                    />
                                </Typography>
                                <Typography
                                    fontFamily="Roboto"
                                    fontSize={14}
                                    letterSpacing={0.17}
                                    color="alert-info.main"
                                >
                                    <FormattedMessage
                                        id="event.registrationClosedDescription"
                                        defaultMessage="Registration for this event has been closed."
                                    />
                                </Typography>
                            </div>
                        </div>
                    );
                }
            case RegistrationCardStatus.UNABLE_TO_REGISTER:
                if (event.user_meets_level_badge_requirements?.meets_requirements === false) {
                    return (
                        <div>
                            <Alert severity="info">
                                <Typography fontFamily="Roboto" fontSize={16} letterSpacing={0.15} fontWeight={500}>
                                    <FormattedMessage id="common.almostThere" defaultMessage="Almost There!" />
                                </Typography>
                                <Typography fontFamily="Roboto" fontSize={14} letterSpacing={0.17} mt={1}>
                                    <FormattedMessage
                                        id="event.buildYourCredentials"
                                        defaultMessage="Build your credentials by completing another event. Keep growing your Victron knowledge and earning badges that demonstrate your expertise."
                                    />
                                </Typography>
                            </Alert>
                            <Button
                                className="rounded-md mt-5 block ml-auto uppercase"
                                disableElevation={false}
                                onClick={handleOpenRequiredEvents}
                            >
                                <FormattedMessage id="event.viewRequiredEvents" defaultMessage="View Required Events" />
                            </Button>
                        </div>
                    );
                }
                return (
                    <div className="bg-light-blue py-3 px-4 rounded flex gap-2 w-full">
                        <InfoOutlinedIcon color="info-main" />
                        <div>
                            <Typography
                                fontFamily="Roboto"
                                fontSize={16}
                                fontWeight={500}
                                letterSpacing={0.15}
                                color="alert-info.main"
                                marginBottom={1}
                            >
                                <FormattedMessage
                                    id="event.ineligible.toRegister"
                                    defaultMessage="You are ineligible to register."
                                />
                            </Typography>
                            <Typography fontFamily="Roboto" fontSize={14} letterSpacing={0.17} color="alert-info.main">
                                <FormattedMessage
                                    id="event.ineligible.info"
                                    defaultMessage="Unfortunately, you're not able to join this event at this time."
                                />
                            </Typography>
                        </div>
                    </div>
                );
            default:
                return <></>;
        }
    }, [
        HybridEventManagementSubscriptionElement,
        event?.enable_external_registration,
        event?.hybrid_event,
        event?.published,
        event?.registration_status,
        handleExternalRegistration,
        handleRegister,
        handleUnregister,
        handleHybridRegisterNow,
        isEventLoading,
        isGuestUserInfoFetching,
        isLazyEventLoading,
        isOppositeEventLoading,
        isRegisterLoading,
        isUnregisterLoading,
        isUserLoading,
        oppositeEvent?.registration_status,
    ]);

    return (
        <Box className="font-inter md:h-full relative">
            <div className="absolute w-full z-10">
                <div className="relative h-[500px] md:h-[529px] w-full">
                    <div className="absolute w-full h-full z-10 bg-fade-left-right" />
                    <img src={EventBanner} className="w-full h-full bg-cultured" />
                </div>
            </div>
            <DottedBackground
                gradient
                gradientValue="20%"
                className="absolute w-full h-full md:h-[700px] lg:h-[600px]"
            />
            <div className="h-full relative gap-10">
                <div className="pt-16 md:px-16 flex flex-col lg:flex-row justify-between gap-32 md:gap-10 md:min-h-[700px] lg:min-h-[600px]">
                    <div className="flex flex-col gap-6 px-3.5 md:px-0 z-10 lg:max-w-[45%] xl:max-w-[60%]">
                        <p className="text-3xl md:text-5xl font-bold break-words">{event?.name}</p>
                        {event?.subtitle ? (
                            <Typography fontFamily="museo-sans" fontSize={24} fontWeight={400}>
                                {event.subtitle}
                            </Typography>
                        ) : null}
                        <p className="bg-spanish-blue-20 py-2 px-5 w-fit rounded text-spanish-blue">
                            <FormattedMessage
                                id="event.organizer"
                                defaultMessage="<b>Organizer:</b> {organizer}"
                                values={{
                                    b: (chunks) => <span className="font-bold">{chunks}</span>,
                                    organizer: event?.owner?.full_name ?? 'N/A',
                                }}
                            />
                        </p>
                    </div>
                    <EventDetailCard RegisterElement={RegistrationActionView} />
                </div>
                <div className="mt-10">
                    <MUITabs
                        value={currentTab}
                        onChange={(_, newValue) => setCurrentTab(newValue)}
                        sx={{
                            overflow: 'unset',
                            '.MuiTabs-scroller': {
                                overflow: 'unset !important',
                            },
                        }}
                    >
                        <Tabs.Filled
                            label={
                                <p className={TAB_CLASSNAME}>
                                    <FormattedMessage id="event.overview" defaultMessage="Event Overview" />
                                </p>
                            }
                            className="flex flex-col gap-10 md:gap-28 md:flex-row"
                        />
                        <Tabs.Filled
                            label={
                                <>
                                    <p className={TAB_CLASSNAME}>
                                        <FormattedMessage id="common.markets" defaultMessage="Markets" />
                                    </p>
                                    <Box
                                        sx={{
                                            backgroundColor: (theme) => theme.palette.primary.main,
                                        }}
                                        className="flex items-center justify-center font-museo font-semibold text-sm absolute w-[24px] h-[24px] rounded-full -top-2 -right-2"
                                    >
                                        <p className="text-white">{event?.markets.length ?? 0}</p>
                                    </Box>
                                </>
                            }
                            className="flex flex-col gap-10 md:gap-28 md:flex-row overflow-visible"
                            sx={{
                                overflow: 'unset',
                            }}
                        />
                    </MUITabs>
                    <div className="py-10 md:py-20 px-3.5 md:px-16 bg-white flex flex-col lg:flex-row gap-5 lg:gap-36 flex-1 h-full">
                        <div className="font-bold text-4xl">
                            {currentTab === 0 ? (
                                <FormattedMessage
                                    id="event.overview.tab"
                                    defaultMessage="<blue>Event</blue> overview"
                                    values={{ blue: (chunks) => <span className="text-spanish-blue">{chunks}</span> }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="event.markets.tab"
                                    defaultMessage="<blue>Related</blue> markets"
                                    values={{ blue: (chunks) => <span className="text-spanish-blue">{chunks}</span> }}
                                />
                            )}
                        </div>
                        {currentTab === 0 ? (
                            <Box className="break-words min-w-0 w-full">
                                <RichTextReadOnly content={event?.description} extensions={extensions} />
                            </Box>
                        ) : event?.markets.length ? (
                            <Box
                                className="grid h-fit gap-5 lg:flex-auto"
                                sx={{
                                    '@media screen and (min-width: 1440px)': {
                                        gridTemplateColumns: `repeat(${
                                            Number(event?.markets.length) <= 3 ? event?.markets.length : 3
                                        }, minmax(0, 1fr))`,
                                    },
                                    '@media screen and (min-width: 970px) and (max-width: 1440px)': {
                                        gridTemplateColumns: `repeat(${
                                            Number(event?.markets.length) <= 2 ? event?.markets.length : 2
                                        }, minmax(0, 1fr))`,
                                    },
                                }}
                            >
                                {event.markets.map((market) => (
                                    <EventDetailMarketCard key={'market-' + market.id} market={market} />
                                ))}
                            </Box>
                        ) : (
                            <div className="w-full flex items-center justify-center">
                                <p className="bg-light-silver-60 py-5 px-8 rounded-xl font-roboto font-semibold text-black-80">
                                    <FormattedMessage
                                        id="event.noRelatedMarkets"
                                        defaultMessage="There are currently no related markets associated to this event"
                                    />
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {registrationActionsContainer
                ? createPortal(
                      <div className="md:hidden px-4 py-7 bg-white w-full z-10 flex flex-col gap-2">
                          {RegistrationActionView}
                      </div>,
                      registrationActionsContainer
                  )
                : null}
            {event?.questionnaire_id && (user || quizzStatus.guestInfo) && !event?.hybrid_event ? (
                <Dialog open={quizzDialogOpen}>
                    <div className={clsx({ hidden: !quizzStatus.show })}>
                        <Widget
                            id={event?.questionnaire_id}
                            iframeProps={{ style: 'border-radius: 0px;' }}
                            height={600}
                            onStarted={handleStarted}
                            onSubmit={handleSubmit}
                            hidden={{
                                user_name: user
                                    ? user.first_name + ' ' + user.last_name
                                    : quizzStatus.guestInfo?.first_name + ' ' + quizzStatus.guestInfo?.last_name,
                                user_email: user ? user.email : quizzStatus.guestInfo?.email ?? '',
                            }}
                            inlineOnMobile
                        />
                    </div>
                    <IconButton className="!absolute right-0" onClick={handleIconClose}>
                        <CloseIcon />
                    </IconButton>
                    {quizzStatus.confirm ? (
                        <div className="p-5">
                            <CancelContent
                                title={intl.formatMessage({
                                    id: 'event.quizz.confirmation',
                                    defaultMessage:
                                        "Your progress won't be saved and you will not be registered for the event",
                                })}
                                primary={{
                                    label: intl.formatMessage({
                                        id: 'event.quizz.closeQuestionnaire',
                                        defaultMessage: 'Close questionnaire',
                                    }),
                                    onClick: handleClose,
                                }}
                                secondary={{
                                    label: intl.formatMessage({ id: 'common.goBack', defaultMessage: 'Go back' }),
                                    onClick: handleGoBack,
                                }}
                            />
                        </div>
                    ) : null}
                </Dialog>
            ) : null}
            {event?.hybrid_event ? (
                <HybridEventDialog
                    open={isHybridEventDialogOpen}
                    subscription={event}
                    registrationDetails={hybridRegistrationDetails}
                    setFailedStatus={setFailedStatus}
                    onClose={() => {
                        setIsHybridEventDialogOpen(false);
                        setHybridRegistrationDetails(undefined);
                    }}
                />
            ) : null}
            <ColleagueDialog open={colleagueDialogOpen} onClose={() => setColleagueDialogOpen(false)} />
            <RegistrationFailedDialog
                open={!!failedStatus}
                status={failedStatus}
                enableWaitingList={event?.enable_waiting_list}
                onClose={() => setFailedStatus(undefined)}
            />
            <UnauthenticatedFormDialog
                open={!!unauthenticatedFormMode}
                onClose={() => {
                    setUnauthenticatedFormMode(null);
                    setIsWaitingListFlow(false);
                }}
                setFailedStatus={setFailedStatus}
                handleUnauthQuizz={handleUnauthQuizz}
                mode={unauthenticatedFormMode ?? undefined}
                handleRegister={register}
                handleJoinWaitingList={joinWaitingListAction}
                handleHybridRegistration={handleHybridRegistrationForm}
                isWaitingListFlow={isWaitingListFlow}
                eventId={eventId}
                eventType={eventType}
                setSearchParams={setSearchParams}
            />
            <RequiredEventsDialog open={requiredEventsDialogOpen} onClose={() => setRequiredEventsDialogOpen(false)} />
            <TemporaryRequiredEventsDialog
                state={temporaryRequiredEventsDialogState}
                onClose={() => {
                    setTemporaryRequiredEventsDialogState(false);
                }}
                handleRegister={handleRegister}
            />
            <BringColleagueDialog
                open={bringAColleagueDialogOpen}
                onClose={() => setBringAColleagueDialogOpen(false)}
            />
        </Box>
    );
};

export { EventDetailCardView };
